"use client";

import { cn } from "@/lib/cn";
import { PlatformError } from "@/lib/api/client";
import { messageFor } from "@/lib/api/messages";
import { useState } from "react";
import { useIdentity } from "@/lib/api/session";
import { useGamesBySlugs } from "@/lib/api/hooks";
import { useBankrolls } from "@/lib/api/transfers";
import { WAGERING_POLL_MS, rollupProgressView, useBonuses, useRollups } from "@/lib/api/incentives";
import {
  freeSpinGameCodes,
  isActiveReward,
  rewardAction,
  rewardView,
  trancheProgressView,
  useAccountRewards,
  useClaimRakeback,
  useClaimRewards,
  useClaimCustomRewards,
  useCustomRewards,
  useRakeback,
  type AccountReward,
} from "@/lib/api/rewards";
import { formatAmount, withCurrency } from "@/lib/format/amount";
import { countdownTag } from "@/lib/smartico/countdown";
import { RewardModal, type RewardModalView } from "@/components/casino/BonusModal";
import { RewardActionButton, RewardCard } from "./RewardCard";
import { Button, Chip, Panel, PanelHeader } from "@/components/ui/primitives";
import { SignedOut } from "@/components/ui/SignedOut";
import { SectionEmpty, SectionError } from "@/components/ui/states";

/* Every mutation on this panel renders its own outcome. Two of them used to
   render nothing at all, which is how two 400-on-every-call bugs reached a
   deployment looking like a button that "does nothing". */
const asPlatformError = (error: unknown) =>
  error instanceof PlatformError ? error : new PlatformError(0, "network", "");

export function YourRewards() {
  const { isSignedIn, isPending } = useIdentity();

  const bankrolls = useBankrolls(isSignedIn);

  const bankroll = bankrolls.data?.at(0);
  const rewards = useAccountRewards(isSignedIn, { refetchInterval: WAGERING_POLL_MS });
  const rakeback = useRakeback(bankroll?.id, isSignedIn);
  const claimRewards = useClaimRewards();
  const claimRakeback = useClaimRakeback(bankroll?.id);
  const custom = useCustomRewards(bankroll?.id, isSignedIn);
  const claimCustom = useClaimCustomRewards(bankroll?.id);
  const rollups = useRollups(isSignedIn, { refetchInterval: WAGERING_POLL_MS });

  /* The bonus behind each reward supplies what the reward itself never
     carries — a name, an icon, an image, a description and terms. The same
     join the whitelabel makes; without it every card was a type label over a
     type glyph, however the operator had configured the bonus. */
  const bonuses = useBonuses();
  const bonusById = new Map((bonuses.data ?? []).map((bonus) => [bonus.id, bonus]));

  const now = Date.now();
  const list = rewards.rewards;

  /* A free spin names its game by provider code, and a game's code is its
     slug — so the codes resolve through the same slug lookup the game page
     uses. Not through `siteGameId`: that is a site-game association id and
     answers 404 from the catalogue every time, which is why no free spin ever
     showed its game before. */
  const spinCodes = [...new Set(list.flatMap(freeSpinGameCodes))];
  const spinGames = useGamesBySlugs(spinCodes, { enabled: isSignedIn });
  const gameBySlug = new Map(spinGames.games.map((game) => [game.slug, game]));

  /* One claim at a time, and only the card that was pressed says Claiming… —
     the whitelabel's useSingleOfferClaim. The batch button below claims many
     and lights every claimable card while it runs. */
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const claimOne = (id: string) => {
    if (claimingId !== null || claimRewards.isPending) return;
    setClaimingId(id);
    claimRewards.mutate([id], { onSettled: () => setClaimingId(null) });
  };

  /* The card is the summary; the kit's reward modal is the detail, and the
     action inside it is the same one the card offers. */
  const [openReward, setOpenReward] = useState<AccountReward | null>(null);

  if (isPending) {
    return (
      <Panel>
        <div className="h-32 animate-pulse rounded-[15px] bg-elevation-2" />
      </Panel>
    );
  }

  if (!isSignedIn) {
    return (
      <Panel>
        <PanelHeader icon="gift" title="Your rewards" />
        <SignedOut
          title="Rewards are tied to your account"
          body="Sign in and this shows what you have earned and what is ready to claim."
        />
      </Panel>
    );
  }

  const scale = bankroll?.token;
  const scaleFor = new Map((bankrolls.data ?? []).map((entry) => [entry.id, entry.token]));
  const rakebackClaimable = rakeback.data?.claimable ?? "0";

  /* The live list; claimed and expired sit in Rewards History below. */
  const active = list.filter((reward) => isActiveReward(reward, now));

  const gameFor = (reward: AccountReward) => {
    const code = freeSpinGameCodes(reward).at(0);
    return code ? gameBySlug.get(code) : undefined;
  };

  /* What each reward's button does right now. A batch claim in flight marks
     every claimable card as claiming; a single claim marks only its own. */
  const actionFor = (reward: AccountReward) => {
    const game = gameFor(reward);
    const batchClaiming = claimRewards.isPending && claimingId === null;
    const claiming = claimingId === reward.id || (batchClaiming && rewardAction(reward, now).kind === "claim");

    return rewardAction(reward, now, {
      claimingId: claiming ? reward.id : null,
      playHref: game?.slug ? `/game/${game.slug}` : undefined,
    });
  };

  /* Only rewards whose button would say Claim: approved, not auto-claimed by
     the platform, not expired. Counting on status alone offered a batch claim
     for rewards already in flight. */
  const claimable = active.filter((reward) => rewardAction(reward, now).kind === "claim");

  /* A wagering reward's real bar comes through its bonus: the reward names the
     bonus, the bonus's rollup states wagered and target. The tranche alone
     never states the target, which is why this join exists — the same one the
     whitelabel makes. */
  const rollupByBonusId = new Map((rollups.data ?? []).map((rollup) => [rollup.bonusId, rollup]));
  const progressFor = (reward: AccountReward) => {
    const tokenScale = scaleFor.get(reward.siteBankrollId);
    const rollup = reward.bonusId ? rollupByBonusId.get(reward.bonusId) : undefined;
    const wp = rollup?.wageringProgress;

    if (rollup && wp) {
      const note = tokenScale
        ? `${withCurrency(formatAmount(wp.wageredAmount, tokenScale), tokenScale.symbol)}/${withCurrency(formatAmount(wp.targetAmount, tokenScale), tokenScale.symbol)}`
        : `${wp.wageredAmount}/${wp.targetAmount}`;
      return { percent: rollupProgressView(rollup).percent, note };
    }

    /* No rollup — the tranche measures itself. Without this the strip fell
       back to no percentage at all, and rendered as a full bar on a reward
       with the whole requirement still to wager. */
    return trancheProgressView(reward.tranche, tokenScale);
  };

  const openView = openReward
    ? (() => {
        const view = rewardView(openReward, now, scaleFor.get(openReward.siteBankrollId));
        const bonus = openReward.bonusId ? bonusById.get(openReward.bonusId) : undefined;
        const countdown =
          !view.isExpired && openReward.expiresAt
            ? countdownTag(Date.parse(openReward.expiresAt), now)
            : null;
        const game = gameFor(openReward);

        return {
          view: {
            title: bonus?.name ?? view.title,
            description: bonus?.description ?? null,
            imageUrl: bonus?.imageUrl ?? null,
            iconUrl: bonus?.iconUrl ?? null,
            terms: bonus?.termsAndConditions ?? null,
            chips: [
              ...(bonus ? [{ label: view.title }] : []),
              ...(view.detail && openReward.status !== "wagering" ? [{ label: view.detail }] : []),
              ...(countdown ? [{ label: countdown.label, urgent: countdown.isUrgent }] : []),
              ...(view.isExpired ? [{ label: "Expired" }] : []),
              ...(game ? [{ label: game.title }] : []),
            ],
            value: view.value,
            progress: (() => {
              const measured = progressFor(openReward);
              return measured ? { percent: measured.percent, label: measured.note } : null;
            })(),
            wageringNote:
              openReward.status === "wagering" && view.detail && !progressFor(openReward)
                ? view.detail
                : null,
          } satisfies RewardModalView,
          action: actionFor(openReward),
        };
      })()
    : null;

  return (
    <Panel>
      <PanelHeader
        icon="gift"
        title="Your rewards"
        action={claimable.length > 0 ? <Chip tone="brand">{claimable.length} loaded to claim</Chip> : undefined}
      />

      {rakeback.data && (
        <div className="border-t border-border px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[12px] font-bold uppercase leading-[1.6] text-fg-subtle">Rakeback</p>
              <p className="text-[15.75px] font-black leading-[1.6] tabular-nums lg:text-[17.5px]">
                {scale ? withCurrency(formatAmount(rakebackClaimable, scale), scale.symbol) : rakebackClaimable}
                <span className="ml-2 text-[12px] font-medium text-fg-subtle">ready to claim</span>
              </p>
            </div>
            <Button
              size="sm"
              disabled={claimRakeback.isPending || rakebackClaimable === "0"}
              onClick={() => claimRakeback.mutate(rakebackClaimable)}
            >
              {claimRakeback.isPending ? "Claiming…" : "Claim rakeback"}
            </Button>
          </div>
          <ClaimOutcome
            inset
            error={claimRakeback.isError ? claimRakeback.error : null}
            success={
              claimRakeback.isSuccess
                ? "Rakeback claimed. It can take a few seconds to update above."
                : null
            }
          />
        </div>
      )}

      {custom.data && (
        <div className="border-t border-border px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[12px] font-bold uppercase leading-[1.6] text-fg-subtle">
                Custom rewards
              </p>
              <p className="text-[15.75px] font-black leading-[1.6] tabular-nums lg:text-[17.5px]">
                {scale ? withCurrency(formatAmount(custom.data.claimable, scale), scale.symbol) : custom.data.claimable}
                <span className="ml-2 text-[12px] font-medium text-fg-subtle">ready to claim</span>
              </p>
              <p className="mt-0.5 text-[12px] leading-[1.6] text-fg-subtle">
                {scale ? withCurrency(formatAmount(custom.data.totalClaimed, scale), scale.symbol) : custom.data.totalClaimed} claimed to date
              </p>
            </div>
            <Button
              size="sm"
              disabled={claimCustom.isPending || custom.data.claimable === "0"}
              onClick={() => claimCustom.mutate()}
            >
              {claimCustom.isPending ? "Claiming…" : "Claim"}
            </Button>
          </div>
          <ClaimOutcome
            inset
            error={claimCustom.isError ? claimCustom.error : null}
            success={
              claimCustom.isSuccess
                ? `${claimCustom.data.data.claimed} custom reward${claimCustom.data.data.claimed === 1 ? "" : "s"} claimed. It can take a few seconds to leave the list above.`
                : null
            }
          />
        </div>
      )}

      {rewards.isPending && <div className="mx-6 my-4 h-20 animate-pulse rounded-[9px] bg-elevation-2" />}

      {rewards.isError && (
        <SectionError
          title="Your rewards didn’t load"
          body={messageFor(asPlatformError(rewards.error), "rewards")}
        />
      )}

      {rewards.isSuccess && active.length === 0 && (
        <SectionEmpty
          icon="gift"
          title="No rewards yet"
          body="Cashback, rakeback and bonuses appear here as soon as they are awarded."
        />
      )}

      {active.length > 0 && (
        <>
          {claimable.length > 0 && (
            <div className="flex items-center justify-between gap-3 border-t border-border px-6 py-4">
              <p className="text-[12px] leading-[1.6] text-fg-subtle">
                {claimable.length} reward{claimable.length === 1 ? "" : "s"} ready to collect.
              </p>
              <Button
                size="sm"
                disabled={claimRewards.isPending}
                onClick={() => claimRewards.mutate(claimable.map((reward) => reward.id))}
              >
                {claimRewards.isPending ? "Claiming…" : `Claim ${claimable.length}`}
              </Button>
            </div>
          )}

          <ul className="grid gap-[8px] border-t border-border px-6 py-4 lg:grid-cols-2">
            {active.map((reward) => (
              <RewardCard
                key={reward.id}
                reward={reward}
                now={now}
                scale={scaleFor.get(reward.siteBankrollId)}
                bonus={reward.bonusId ? bonusById.get(reward.bonusId) : undefined}
                gameName={gameFor(reward)?.title}
                progress={progressFor(reward)}
                action={actionFor(reward)}
                onClaim={() => claimOne(reward.id)}
                onOpen={() => setOpenReward(reward)}
              />
            ))}
          </ul>

          {rewards.hasNextPage && (
            <div className="flex justify-center border-t border-border px-6 py-4">
              <Button
                size="sm"
                variant="outline"
                disabled={rewards.isFetchingNextPage}
                onClick={() => rewards.fetchNextPage()}
              >
                {rewards.isFetchingNextPage ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </>
      )}

      <ClaimOutcome
        error={claimRewards.isError ? claimRewards.error : null}
        success={
          claimRewards.isSuccess
            ? `${claimRewards.data.data.length} reward${claimRewards.data.data.length === 1 ? "" : "s"} claimed. It can take a few seconds to leave the list above.`
            : null
        }
      />

      <RewardModal
        view={openView?.view ?? null}
        onClose={() => setOpenReward(null)}
        action={
          openView && openReward ? (
            <RewardActionButton
              action={openView.action}
              onClaim={() => {
                claimOne(openReward.id);
                setOpenReward(null);
              }}
              className="w-full"
            />
          ) : undefined
        }
      />
    </Panel>
  );
}

/* A claim that worked and a claim that failed used to look identical: the list
   refetched and nothing said which had happened. */
function ClaimOutcome({
  error,
  success,
  inset,
}: {
  error?: unknown;
  success: string | null;
  inset?: boolean;
}) {
  if (error) {
    return (
      <p role="alert" className={cn(inset ? "mt-[8px]" : "px-6 py-3", "text-[12px] text-error-fg")}>
        {messageFor(asPlatformError(error), "rewards", { operation: "write" })}
      </p>
    );
  }

  if (success) {
    return (
      <p className={cn(inset ? "mt-[8px]" : "px-6 py-3", "text-[12px] text-success-fg")}>{success}</p>
    );
  }

  return null;
}
